import { useParams, Link, Navigate } from "react-router-dom";
import { useState } from "react";
import { BOATS, CONTACT } from "../data/boats";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Lightbox from "../components/Lightbox";
import GalleryGrid from "../components/GalleryGrid";
import { BookingWidgetCard } from "../components/BookingWidget";
import { ArrowLeft, Mail, MapPin, Users, BedDouble, Bath, Images } from "lucide-react";
import * as Icons from "lucide-react";

export default function BoatDetail() {
  const { slug } = useParams();
  const boat = BOATS.find((b) => b.slug === slug);
  const [activeIdx, setActiveIdx] = useState(0);
  const [gridOpen, setGridOpen] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  if (!boat) return <Navigate to="/" replace />;
  const isComingSoon = boat.status === "coming_soon";

  // Level 1 → 2: open tiled grid
  const openGrid = () => setGridOpen(true);

  // Level 2 → 3: open single-image lightbox from grid
  const openLightboxAt = (idx) => {
    setActiveIdx(idx);
    setGridOpen(false);
    setLightboxOpen(true);
  };

  return (
    <div data-testid="boat-detail-page" className="bg-[#FAFAF8] min-h-screen">
      <Header solid />

      {/* Back link */}
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 pt-6 pb-4">
        <Link
          to="/"
          data-testid="boat-back-link"
          className="inline-flex items-center gap-2 text-[0.68rem] font-medium uppercase tracking-[0.18em] text-[#6B6B65] hover:text-[#1C1C1A] transition-colors"
        >
          <ArrowLeft size={13} /> All houseboats
        </Link>
      </div>

      {/* Level 1 — 2-image preview, full bleed (Plum Guide style) */}
      <div className="pb-0">
        <div className="relative grid grid-cols-1 md:grid-cols-2 gap-1 h-[60vh] min-h-[380px]">
          {/* Left — large hero */}
          <button
            type="button"
            onClick={openGrid}
            data-testid="boat-active-image-open"
            aria-label="View all photos"
            className="relative overflow-hidden img-zoom-wrap cursor-pointer h-full"
          >
            <img
              src={boat.gallery[0]}
              alt={boat.name}
              data-testid="boat-active-image"
              className={`w-full h-full object-cover transition-transform duration-700 hover:scale-[1.02] ${isComingSoon ? "grayscale opacity-70" : ""}`}
            />
          </button>

          {/* Right — second image with "see all" badge */}
          {boat.gallery.length >= 2 ? (
            <button
              type="button"
              onClick={openGrid}
              data-testid="gallery-thumb-1"
              aria-label="View all photos"
              className="relative hidden md:block overflow-hidden img-zoom-wrap cursor-pointer h-full"
            >
              <img
                src={boat.gallery[1]}
                alt=""
                className={`w-full h-full object-cover transition-transform duration-700 hover:scale-[1.02] ${isComingSoon ? "grayscale opacity-70" : ""}`}
              />
              {/* See all photos badge */}
              <div className="absolute bottom-4 right-4">
                <span
                  data-testid="view-all-photos"
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-white text-[#1C1C1A] text-[0.63rem] font-medium uppercase tracking-[0.16em] shadow-md hover:bg-[#1C1C1A] hover:text-white transition-colors"
                >
                  <Images size={12} /> See all {boat.gallery.length} photos
                </span>
              </div>
            </button>
          ) : null}

          {/* Mobile "see all" badge on left image */}
          <div className="md:hidden absolute bottom-4 right-4">
            <button
              type="button"
              onClick={openGrid}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-white text-[#1C1C1A] text-[0.63rem] font-medium uppercase tracking-[0.16em] shadow-md"
            >
              <Images size={12} /> See all {boat.gallery.length} photos
            </button>
          </div>

          {isComingSoon && (
            <div className="absolute inset-0 bg-[#1C1C1A]/30 flex items-center justify-center pointer-events-none">
              <p className="font-serif text-white text-4xl sm:text-6xl font-light italic">Coming soon</p>
            </div>
          )}
        </div>
      </div>

      {/* Content — below full-bleed gallery */}
      <section className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 py-10 sm:py-14 grid grid-cols-1 lg:grid-cols-12 gap-12">
        {/* Description */}
        <div className="lg:col-span-7">
          {/* Title block — mirrors Plum Guide: breadcrumb → h1 → specs */}
          <p className="eyebrow text-[#6B6B65] mb-2">{boat.location.split(",")[0]}</p>
          <h1 className="font-serif text-4xl sm:text-5xl font-light text-[#1C1C1A] tracking-tight leading-tight">
            {boat.name}
          </h1>
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs text-[#6B6B65] uppercase tracking-wider mt-4 mb-8">
            <span className="inline-flex items-center gap-1.5"><Users size={13} strokeWidth={1.5} /> {boat.guests} guests</span>
            <span className="text-[#1C1C1A]/20">·</span>
            <span className="inline-flex items-center gap-1.5"><BedDouble size={13} strokeWidth={1.5} /> {boat.bedrooms} bedroom</span>
            <span className="text-[#1C1C1A]/20">·</span>
            <span className="inline-flex items-center gap-1.5"><Bath size={13} strokeWidth={1.5} /> {boat.bathrooms} bathroom</span>
          </div>

          <p className="text-[#6B6B65] leading-relaxed text-sm font-light">{boat.description}</p>

          <div className="mt-12 space-y-6">
            {boat.highlights.map((h) => (
              <div key={h.title} className="flex gap-5 items-start border-b border-[#1C1C1A]/8 pb-6">
                <div className="w-5 h-px bg-[#C8A96E] mt-3 shrink-0" />
                <div>
                  <p className="font-medium text-[#1C1C1A] text-sm">{h.title}</p>
                  <p className="mt-1 text-sm text-[#6B6B65] leading-relaxed font-light">{h.body}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Amenities */}
          {boat.amenities?.length > 0 && (
            <div className="mt-14">
              <p className="eyebrow mb-6">What's onboard</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-0 border-l border-t border-[#1C1C1A]/8">
                {boat.amenities.map((a) => {
                  const Icon = Icons[a.icon] || Icons.Check;
                  return (
                    <div key={a.label} className="flex items-center gap-3 p-4 sm:p-5 border-r border-b border-[#1C1C1A]/8 hover:bg-[#F5F3EE] transition-colors">
                      <Icon size={15} strokeWidth={1.25} className="text-[#C8A96E] shrink-0" />
                      <p className="text-sm text-[#1C1C1A] font-light">{a.label}</p>
                    </div>
                  );
                })}
              </div>
              <p className="mt-5 text-xs text-[#6B6B65]">
                The marina is a working environment. Water safety is paramount — strictly no swimming within the marina.
              </p>
            </div>
          )}
        </div>

        {/* Booking sidebar */}
        <aside className="lg:col-span-4 lg:col-start-9">
          {isComingSoon ? (
            <div data-testid="coming-soon-card" className="bg-[#F5F3EE] p-8 border border-[#1C1C1A]/8">
              <p className="eyebrow mb-3">Not yet bookable</p>
              <h3 className="font-serif text-2xl font-light text-[#1C1C1A] leading-tight">
                Aquastays Lily<br /><em className="italic">launches soon.</em>
              </h3>
              <p className="mt-4 text-[#6B6B65] leading-relaxed text-sm font-light">
                We'll email you the moment she's ready for guests. No spam — just one note from Dru.
              </p>
              <a
                href={`mailto:${CONTACT.email}?subject=Aquastays%20Lily%20launch%20list`}
                data-testid="coming-soon-cta"
                className="btn-primary mt-6 w-full justify-center"
              >
                <Mail size={14} /> Add me to the list
              </a>
              <div className="mt-5 flex items-center gap-2 text-xs text-[#6B6B65]">
                <MapPin size={13} strokeWidth={1.5} className="text-[#C8A96E]" /> {boat.location}
              </div>
            </div>
          ) : (
            <div id="booking" data-testid="boat-booking-card" className="bg-[#F5F3EE] p-8 border border-[#1C1C1A]/8">
              <p className="eyebrow mb-3">Book your stay</p>
              <h3 className="font-serif text-2xl font-light text-[#1C1C1A] leading-tight mb-6">
                <em className="italic">Pick a date to drift off.</em>
              </h3>
              <BookingWidgetCard />
              <div className="mt-6 flex items-center gap-2 text-xs text-[#6B6B65]">
                <MapPin size={13} strokeWidth={1.5} className="text-[#C8A96E]" /> {boat.location}
              </div>
            </div>
          )}
        </aside>
      </section>

      <Footer />

      <Lightbox
        images={boat.gallery}
        activeIdx={activeIdx}
        open={lightboxOpen}
        onChange={setActiveIdx}
        onClose={() => setLightboxOpen(false)}
        grayscale={isComingSoon}
        alt={boat.name}
      />

      {/* Level 2 — tiled grid (all photos) */}
      <GalleryGrid
        images={boat.gallery}
        open={gridOpen}
        onClose={() => setGridOpen(false)}
        onSelect={openLightboxAt}
        alt={boat.name}
      />
    </div>
  );
}
